import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/ui-components'
import { Description } from 'components/Text'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useDynamicDocumentsMutation } from 'hooks/mutations/useDynamicDocumentsMutation'
import { MappedErrorMessageReturn } from 'hooks/useMappedErrorMessage'
import { FormSubmitButton } from 'pages/Verification/_components/FormSubmitButton'
import { FormSubmitError } from 'pages/Verification/_components/FormSubmitError'
import { StepDescription } from 'pages/Verification/_components/StepDescription'
import { DynamicDocumentPayload } from 'types/dynamicFields'
import { BaseErrorResponse } from 'types/errors'
import {
  DocumentActions,
  DocumentCategoryKey,
  RequiredDocuments,
  UploadedDocument,
} from '../helpers/types'
import { getDocumentPayload } from '../helpers/utils'
import { CategoryDocuments } from './CategoryDocuments'

export interface UploadDocumentsFormData {
  requiredCategories: DocumentCategoryKey[]
  requiredDocuments: RequiredDocuments
  uploadedDocuments: UploadedDocument[]
  documentActions: DocumentActions
  errorState: Partial<MappedErrorMessageReturn>
}

export const UploadDocumentsForm = ({
  requiredCategories,
  requiredDocuments,
  uploadedDocuments,
  documentActions,
  errorState,
}: UploadDocumentsFormData) => {
  const { t } = useTranslation()
  const { setUnsavedChanges, submitStep } = useVerificationContext()
  const { requestError, handleRequestError, resetRequestError } = errorState || {}
  const { mutate: submitDynamicDocuments, isPending: isSubmittingDocuments } =
    useDynamicDocumentsMutation()

  const [isMissingDocumentsError, setIsMissingDocumentsError] = useState(false)

  const requiredDocumentList = Object.values(requiredDocuments)?.flat()
  const providedDocumentCount = requiredDocumentList?.filter(({ provided }) => provided)?.length
  const uploadedDocumentCount = uploadedDocuments?.length
  const hasAllRequiredDocuments =
    requiredDocumentList?.length === providedDocumentCount + uploadedDocumentCount

  const onSubmitDocuments = () => {
    resetRequestError()
    if (!hasAllRequiredDocuments) {
      setIsMissingDocumentsError(true)
      return
    }
    const documentsPayload: DynamicDocumentPayload[] = uploadedDocuments?.map(document =>
      getDocumentPayload(document)
    )
    submitDynamicDocuments(documentsPayload, {
      onSuccess: submitStep,
      onError: (error: BaseErrorResponse) => {
        handleRequestError(error, 'submitDynamicDocuments')
        documentActions?.refetchDocuments()
      },
    })
  }

  useEffect(() => {
    setUnsavedChanges(!!uploadedDocumentCount)
  }, [uploadedDocumentCount])

  useEffect(() => {
    if (isMissingDocumentsError && hasAllRequiredDocuments) {
      setIsMissingDocumentsError(false)
    }
  }, [hasAllRequiredDocuments])

  return (
    <Box flexCol gap={24}>
      <StepDescription
        paragraphs={[
          t('uploadDocuments.introduction.requirements'),
          t('uploadDocuments.introduction.tailoredToIndustry'),
          t('uploadDocuments.introduction.correctDocumentation'),
        ]}
      />
      {(isMissingDocumentsError || requestError?.show) && (
        <FormSubmitError
          {...(requestError?.show && {
            errorCode: requestError?.errorCode,
            sentryTraceId: requestError?.sentryTraceId,
          })}
        >
          {isMissingDocumentsError && t('uploadDocument.missingDocumentsError')}
        </FormSubmitError>
      )}
      <Box>
        {!!requiredCategories?.length ? (
          requiredCategories?.map((category: DocumentCategoryKey) => (
            <CategoryDocuments
              key={category}
              category={category}
              requiredDocuments={requiredDocuments?.[category]}
              uploadedDocuments={uploadedDocuments?.filter(
                ({ documentId }) => documentId?.category === category
              )}
              documentActions={documentActions}
            />
          ))
        ) : (
          <Description>{t('uploadDocuments.requiredDocumentsEmpty')}</Description>
        )}
        <Box paddingT={16}>
          <FormSubmitButton
            loading={isSubmittingDocuments}
            disabled={isMissingDocumentsError}
            onClick={onSubmitDocuments}
            testId='continue-button'
          />
        </Box>
      </Box>
    </Box>
  )
}
